import { Search, Download, ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";

/**
 * Barre d'outils unifiée pour toutes les tables KPI (Achats, CA Daily, RH, Corner).
 * - Chips de vues (Vue Globale / Vue Corner / Vue Secteur / Vue DR) à gauche
 * - Barre de recherche centrale
 * - Chips de statut (Tous / En avance / Dans l'objectif / En retard) à droite
 * - Bouton "Exporter la vue" tout à droite
 *
 * Props:
 *   viewMode, setViewMode, viewOptions=[{k,label}]
 *   q, setQ, searchTestid, placeholder
 *   status, setStatus, statusOptions=[{k,label,count?}]
 *   onExport, exportTestid, exportLabel="Exporter la vue"
 *   viewTestidPrefix, statusTestidPrefix
 *   leftSlot, rightSlot (nodes optionnels)
 */
export function KpiTableToolbar({
  viewMode, setViewMode, viewOptions,
  q, setQ, searchTestid = "kpi-toolbar-search", placeholder = "Rechercher…",
  status, setStatus, statusOptions,
  onExport, exportTestid = "kpi-toolbar-export", exportLabel = "Exporter la vue",
  viewTestidPrefix = "kpi-view", statusTestidPrefix = "kpi-status",
  leftSlot, rightSlot,
}) {
  return (
    <div className="p-3 border-b border-slate-100 flex flex-col md:flex-row md:items-center gap-2 md:gap-3 flex-wrap">
      {/* LEFT — vue chips */}
      <div className="flex items-center gap-2 flex-wrap min-w-0">
        {viewOptions && setViewMode && (
          <div className="flex items-center gap-0.5 bg-slate-100 rounded-lg p-0.5">
            {viewOptions.map(v => (
              <button key={v.k}
                      onClick={() => setViewMode(v.k)}
                      data-testid={`${viewTestidPrefix}-${v.k}`}
                      className={`text-[11px] px-2.5 py-1 rounded-md font-semibold whitespace-nowrap transition-colors ${
                        viewMode === v.k
                          ? "bg-white shadow-sm text-slate-900"
                          : "text-slate-500 hover:text-slate-800"
                      }`}>
                {v.label}
              </button>
            ))}
          </div>
        )}
        {leftSlot}
      </div>

      {/* CENTER — search */}
      <div className="relative flex-1 md:flex-none md:mx-auto">
        <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400"/>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder={placeholder}
               data-testid={searchTestid}
               className="pl-8 pr-3 py-1.5 rounded-full border border-slate-200 text-xs w-full md:w-64 outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-400"/>
      </div>

      {/* RIGHT — status chips + export */}
      <div className="flex items-center gap-2 flex-wrap md:ml-auto">
        {statusOptions && setStatus && (
          <div className="flex items-center gap-0.5 bg-slate-100 rounded-lg p-0.5">
            {statusOptions.map(s => (
              <button key={s.k}
                      onClick={() => setStatus(s.k)}
                      data-testid={`${statusTestidPrefix}-${s.k}`}
                      className={`text-[11px] px-2.5 py-1 rounded-md font-semibold whitespace-nowrap transition-colors ${
                        status === s.k
                          ? "bg-white shadow-sm text-slate-900"
                          : `text-slate-500 hover:text-slate-800 ${s.color || ""}`
                      }`}>
                {s.label}
                {s.count != null && (
                  <span className={`ml-1 text-[9px] font-bold px-1 rounded ${
                    status === s.k ? "bg-slate-100 text-slate-700" : "bg-slate-200 text-slate-500"
                  }`}>{s.count}</span>
                )}
              </button>
            ))}
          </div>
        )}
        {rightSlot}
        {onExport && (
          <button onClick={onExport}
                  data-testid={exportTestid}
                  className="flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 text-slate-700 whitespace-nowrap">
            <Download className="w-3.5 h-3.5"/> {exportLabel}
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Entête de colonne triable avec cycle bidirectionnel visuel :
 *   ChevronsUpDown (neutre) → ChevronUp (asc) → ChevronDown (desc)
 * Le composant est stateless : le parent gère sortKey/sortDir et onSort.
 */
export function SortHeadCell({
  k, label, align = "left", sortKey, sortDir, onSort, testid, className = "", title,
}) {
  const active = sortKey === k;
  const Icon = active ? (sortDir === "asc" ? ChevronUp : ChevronDown) : ChevronsUpDown;
  return (
    <button type="button"
            onClick={() => onSort(k)}
            title={title}
            data-testid={testid || `sort-${k}`}
            className={`text-[9px] font-bold uppercase tracking-wider text-slate-500 cursor-pointer hover:text-slate-800 select-none inline-flex items-center gap-1 whitespace-nowrap w-full ${
              align === "right" ? "justify-end pr-3" : align === "center" ? "justify-center" : "pl-1"
            } ${className}`}>
      <span className="truncate">{label}</span>
      <Icon className={`w-3 h-3 shrink-0 ${active ? "text-blue-600" : "text-slate-300"}`}/>
    </button>
  );
}

/**
 * Hook helper : gère sortKey / sortDir et le tri de rows.
 *   const { sortKey, sortDir, toggleSort, sorted } = useKpiSort(rows, initialKey, initialDir, comparator);
 * comparator: (a,b,key) => number  (pas de multiplication par direction, gérée en interne)
 */
import { useState, useMemo } from "react";
export function useKpiSort(rows, initialKey, initialDir = "desc", comparator) {
  const [sortKey, setSortKey] = useState(initialKey);
  const [sortDir, setSortDir] = useState(initialDir);
  const toggleSort = (k) => {
    if (sortKey === k) {
      setSortDir(d => d === "asc" ? "desc" : "asc");
    } else {
      setSortKey(k);
      setSortDir("desc");
    }
  };
  const sorted = useMemo(() => {
    if (!rows || !rows.length) return rows || [];
    const dir = sortDir === "asc" ? 1 : -1;
    const arr = [...rows];
    arr.sort((a, b) => {
      const raw = comparator(a, b, sortKey);
      // string comparator peut retourner déjà signé — on multiplie par dir.
      return raw * dir;
    });
    return arr;
  }, [rows, sortKey, sortDir, comparator]);
  return { sortKey, sortDir, toggleSort, sorted, setSortKey, setSortDir };
}
